import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { fetchCSRFToken } from "../../../components/common/utilities/api";

export interface InlineDatasetInfo {
    id: number;
    name: string;
    type: string;
    status: string;
    status_message: string;
    raster_min: number | null;
    raster_max: number | null;
}

const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 30; // poll every 2s for at most 1 minute

const toDatasetInfo = (data: InlineDatasetInfo): InlineDatasetInfo => ({
    id: data.id,
    name: data.name,
    type: data.type,
    status: data.status,
    status_message: data.status_message,
    raster_min: data.raster_min,
    raster_max: data.raster_max,
});

/**
 * Encapsulates the INLINE dataset lifecycle: listing partner datasets, uploading
 * a new GeoTIFF and polling its reprojection status until it is done or failed.
 */
export function useInlineDatasets() {
    const [uploadedDataset, setUploadedDataset] = useState<InlineDatasetInfo | null>(null);
    const [availableDatasets, setAvailableDatasets] = useState<InlineDatasetInfo[]>([]);
    const [isUploadingDataset, setIsUploadingDataset] = useState<boolean>(false);
    const pollCleanup = useRef<(() => void) | null>(null);

    const fetchAvailableDatasets = useCallback(() => {
        fetch('/api/inline/datasets/available/', { credentials: "include" })
            .then((r) => (r.ok ? r.json() : []))
            .then((data) => setAvailableDatasets(Array.isArray(data) ? data : []))
            .catch((e) => console.warn("Failed to fetch available datasets", e));
    }, []);

    const pollDatasetStatus = useCallback((id: number) => {
        let attempts = 0;
        const interval = setInterval(() => {
            attempts += 1;
            if (attempts > MAX_POLL_ATTEMPTS) {
                clearInterval(interval);
                toast.warning("Still processing your dataset. Please check back later.");
                return;
            }
            fetch(`/api/inline/datasets/${id}/`, { credentials: "include" })
                .then((r) => {
                    if (!r.ok) {
                        throw new Error(`HTTP ${r.status}`);
                    }
                    return r.json();
                })
                .then((data) => {
                    setUploadedDataset(toDatasetInfo(data));
                    if (data.status === "done" || data.status === "failed") {
                        clearInterval(interval);
                        if (data.status === "done") {
                            toast.success(data.status_message);
                        } else {
                            toast.error(data.status_message);
                        }
                    }
                })
                .catch((e) => {
                    console.warn("Failed to poll dataset status", e);
                    clearInterval(interval);
                    toast.error("Lost contact with server while processing dataset.");
                });
        }, POLL_INTERVAL_MS);
        pollCleanup.current = () => clearInterval(interval);
    }, []);

    const uploadDataset = useCallback(async (file: File | null | undefined, name: string, description: string, type: string) => {
        if (!file) {
            toast.error("Please select a .tif file to upload.");
            return;
        }
        const trimmedName = name.trim();
        if (!trimmedName) {
            toast.error("Dataset name is required.");
            return;
        }
        const lower = file.name.toLowerCase();
        if (!(lower.endsWith(".tif") || lower.endsWith(".tiff"))) {
            toast.error("Only GeoTIFF (.tif/.tiff) files are supported.");
            return;
        }

        const formData = new FormData();
        formData.append("file", file);
        formData.append("name", trimmedName);
        formData.append("description", description);
        formData.append("type", type);

        setIsUploadingDataset(true);
        const csrfToken = await fetchCSRFToken();
        fetch('/api/inline/datasets/', {
            method: "POST",
            headers: { "X-CSRFToken": csrfToken },
            credentials: "include",
            body: formData,
        }).then((response) => {
            if (response.ok) {
                return response.json().then((data) => {
                    setIsUploadingDataset(false);
                    setUploadedDataset(toDatasetInfo(data));
                    toast.info(`Dataset "${data.name}" uploaded successfully.`);
                    if (data.status_message) {
                        toast.info(data.status_message);
                    }
                    pollCleanup.current?.();
                    pollDatasetStatus(data.id);
                });
            } else if (response.status === 413) {
                setIsUploadingDataset(false);
                toast.error("File is too large.");
            } else {
                response.json().then((data) => {
                    setIsUploadingDataset(false);
                    const message = data?.file || data?.detail || data?.message || JSON.stringify(data) || "Unknown error";
                    toast.error("Error uploading dataset: " + message);
                }).catch(() => {
                    setIsUploadingDataset(false);
                    toast.error("Error uploading dataset.");
                });
            }
        }).catch((error) => {
            setIsUploadingDataset(false);
            toast.error("Error uploading dataset: " + (error instanceof Error ? error.message : String(error)));
        });
    }, [pollDatasetStatus]);

    useEffect(() => () => pollCleanup.current?.(), []);

    return {
        uploadedDataset,
        setUploadedDataset,
        availableDatasets,
        isUploadingDataset,
        fetchAvailableDatasets,
        uploadDataset,
    };
}
